import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { AccountType, IAccount } from '../../interfaces/accounting.interface';
import { formatMoney } from './money';
import { dayLabel } from './closingLabels';
import { useAccountingInstitution } from './useAccountingInstitution';

const TYPES: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

interface FormState {
    id?: string;
    code: string;
    name: string;
    type: AccountType;
    parentId: string | null;
    cashOrBank: boolean;
    active: boolean;
}

const EMPTY: FormState = { code: '', name: '', type: 'EXPENSE', parentId: null, cashOrBank: false, active: true };

/**
 * The chart of accounts grouped by type. System accounts (the ones postings rely on) can be
 * renamed but not deleted; the server also refuses to delete an account that has postings.
 */
const ChartOfAccounts: React.FC = () => {
    const institutionId = useAccountingInstitution();
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [accounts, setAccounts] = useState<IAccount[]>([]);
    const [form, setForm] = useState<FormState | null>(null);
    const [saving, setSaving] = useState(false);
    // The first day of the open fiscal year, once an earlier year has been closed.
    const [countedFrom, setCountedFrom] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!institutionId) return;
        AccountingService.listAccounts(institutionId)
            .then(setAccounts)
            .catch(error => showApiError(error, t('errors.loadAccounts')));
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(load, [load]);

    useEffect(() => {
        if (!institutionId) return;
        let current = true;
        (async () => {
            try {
                const y = await AccountingService.yearClosingStatus(institutionId);
                if (current) setCountedFrom(y?.next && y.history.some(h => h.status === 'CLOSED') ? y.next.start : null);
            } catch {
                // Only explains the balances, so the page works without it.
                if (current) setCountedFrom(null);
            }
        })();
        return () => { current = false; };
    }, [institutionId]);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        try {
            if (form.id) {
                await AccountingService.updateAccount(institutionId, form.id,
                    { name: form.name, parentId: form.parentId, active: form.active });
                showSuccess(t('chartOfAccounts.updated'));
            } else {
                await AccountingService.createAccount(institutionId, {
                    code: form.code, name: form.name, type: form.type, parentId: null,
                    cashOrBank: form.type === 'ASSET' && form.cashOrBank,
                });
                showSuccess(t('chartOfAccounts.created'));
            }
            setForm(null);
            load();
        } catch (error) {
            showApiError(error, t('chartOfAccounts.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (a: IAccount) => {
        if (!window.confirm(t('chartOfAccounts.confirmDelete', { code: a.code, name: a.name }))) return;
        try {
            await AccountingService.deleteAccount(institutionId, a.id);
            showSuccess(t('chartOfAccounts.deleted'));
            load();
        } catch (error) {
            showApiError(error, t('chartOfAccounts.deleteFailed'));
        }
    };

    const editing = !!form?.id;

    return (
        <Box sx={{ p: 3, maxWidth: 1000 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>{t('chartOfAccounts.title')}</Typography>
                <Button variant="contained" onClick={() => setForm({ ...EMPTY })}>{t('chartOfAccounts.newAccount')}</Button>
            </Stack>
            {countedFrom && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('chartOfAccounts.countedFrom', { date: dayLabel(countedFrom) })}
                </Typography>
            )}
            {TYPES.map(type => {
                const rows = accounts.filter(a => a.type === type);
                if (rows.length === 0) return null;
                return (
                    <Paper key={type} variant="outlined" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5 }}>{t(`accountTypes.${type}`)}</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell width={90}>{t('fields.code')}</TableCell>
                                    <TableCell>{t('common:fields.name')}</TableCell>
                                    <TableCell align="right">{t('fields.balance')}</TableCell>
                                    <TableCell width={100} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(a => (
                                    <TableRow key={a.id} sx={{ opacity: a.active ? 1 : 0.5 }}>
                                        <TableCell>{a.code}</TableCell>
                                        <TableCell>
                                            {a.name}
                                            {a.cashOrBank && <Chip size="small" label={t('chips.cashBank')} sx={{ ml: 1 }} />}
                                            {!a.active && <Chip size="small" label={t('chips.inactive')} sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell align="right">{formatMoney(a.balance)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={t('common:actions.edit')}>
                                                <IconButton size="small" aria-label={t('chartOfAccounts.editAria', { name: a.name })} onClick={() => setForm({
                                                    id: a.id, code: a.code, name: a.name, type: a.type,
                                                    parentId: a.parentId ?? null, cashOrBank: a.cashOrBank, active: a.active,
                                                })}><EditIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            {!a.systemKey && (
                                                <Tooltip title={t('common:actions.delete')}>
                                                    <IconButton size="small" aria-label={t('chartOfAccounts.deleteAria', { name: a.name })}
                                                                onClick={() => remove(a)}><DeleteIcon fontSize="small" /></IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                );
            })}

            <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="xs">
                <DialogTitle>{editing ? t('chartOfAccounts.editAccount') : t('chartOfAccounts.newAccount')}</DialogTitle>
                {form && (
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField id="account-code" label={t('fields.code')} required disabled={editing} value={form.code}
                                       onChange={e => setForm({ ...form, code: e.target.value })} />
                            <TextField id="account-name" label={t('common:fields.name')} required value={form.name}
                                       onChange={e => setForm({ ...form, name: e.target.value })} />
                            <TextField id="account-type" select label={t('common:fields.type')} disabled={editing} value={form.type}
                                       onChange={e => setForm({ ...form, type: e.target.value as AccountType })}
                                       SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
                                {TYPES.map(type => <option key={type} value={type}>{t(`accountTypes.${type}`)}</option>)}
                            </TextField>
                            {!editing && form.type === 'ASSET' && (
                                <FormControlLabel label={t('chartOfAccounts.isCashOrBank')} control={
                                    <Checkbox checked={form.cashOrBank}
                                              onChange={e => setForm({ ...form, cashOrBank: e.target.checked })} />
                                } />
                            )}
                            {editing && (
                                <FormControlLabel label={t('common:status.active')} control={
                                    <Checkbox checked={form.active}
                                              onChange={e => setForm({ ...form, active: e.target.checked })} />
                                } />
                            )}
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setForm(null)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={save}
                            disabled={saving || !form?.name.trim() || !form?.code.trim()}>
                        {editing ? t('common:actions.save') : t('common:actions.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChartOfAccounts;
